import ENV from "@/utils/env";
import { SceneMetric } from "../stores/use-reports-scene-store";
import { Visualisation } from "@/api/renderer/types/renderer.type";

/* Constants */
const BASE_URL = `${ENV.API_PLATFORM_URL}/pipeline/renderer/images/v1`;
const DEFAULT_WIDTH = 1024;
const DEFAULT_HEIGHT = 1024;

/**
 * Options for scene render URL generation
 */
export interface SceneRenderUrlOptions {
  metric?: SceneMetric;
  visualisation?: Visualisation;
  showAOI?: boolean;
  showGazePath?: boolean;
}

/**
 * Get the scene render URL with AOI regions and metric overlays
 *
 * @param projectId - The project ID
 * @param studyId - The study ID
 * @param sceneKey - The scene/asset key
 * @param options - Optional configuration for the render URL
 * @returns The URL for the rendered scene image
 */
export function getSceneRenderUrl(projectId: string, studyId: string, sceneKey: string, options: SceneRenderUrlOptions = {}): string {
  const { metric = "vis", visualisation = "heat", showAOI = true, showGazePath = false } = options;

  let endpoint = "";
  const additionalParams: Record<string, string | boolean> = {
    metric,
  };

  if (showAOI || showGazePath) {
    // Scenes only have AOI (region) data, not hotspot data, so both AOIs and the
    // gaze path (which is derived from ranked regions) must go through the regions
    // endpoint - the hotspots endpoint has nothing to render for a scene.
    endpoint = "/attention/regions";
    additionalParams.visual = visualisation;
    additionalParams.regions = showAOI;
    additionalParams.ranked = showGazePath;
  } else if (visualisation !== "none") {
    // Use heatmaps endpoint if only heatmap/opacity is requested (no AOIs, no Gaze path)
    endpoint = "/attention/heatmaps";
    additionalParams.visual = visualisation;
  } else {
    // Base image if nothing else is requested
    endpoint = "";
  }

  const url = new URL(`${BASE_URL}${endpoint}`);

  // Add required base params
  url.searchParams.set("prefix", `${projectId}/${studyId}`);
  url.searchParams.set("key", sceneKey);
  url.searchParams.set("maxWidth", DEFAULT_WIDTH.toString());
  url.searchParams.set("maxHeight", DEFAULT_HEIGHT.toString());

  // Add additional params (only if endpoint is not base image or if they are relevant)
  if (endpoint !== "") {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value.toString());
      }
    });
  }

  return url.toString();
}
